import React, { forwardRef, memo, useCallback, useEffect, useRef, useState, } from 'react';
import { BorderRadiusTokens, ColorTokens, FontFamilyTokens, FontSizeTokens, IconSizeTokens, IconTokens, InputStateToken, SizeTypeTokens, StackAlignItems, StackJustifyContent, StackType, } from '@voltmoney/schema';
import { Typography } from '../typography/Typography';
import { Stack } from '../stack/Stack';
import { Icon } from '../icon/Icon';
import { ActivityIndicator, InteractionManager, Platform, Pressable, StyleSheet, TextInput } from 'react-native';
import { fontSizeTokensMap } from '@voltmoney/theme/lib/light-mobile/typography';
import { Space } from '../space/Space';
import isEmpty from 'lodash-es/isEmpty';
import ThemeProvider from '@voltmoney/theme';
import BackgroundTimer from 'react-native-background-timer';
import { isWeb } from "../pages/utils";
/**
 * Raw Component with Derived props + Theme
 */
const TextInputOtp = forwardRef(({ id, state, value, onChangeText, action, triggerAction, isDarkMode, errorLabel, otpLimitExceeded = false, border = {
    color: ColorTokens.Grey_Smoke,
    radius: BorderRadiusTokens.BR1,
    size: 1,
    colorError: ColorTokens.System_Error,
    colorSuccess: ColorTokens.System_Happy,
    colorFocus: ColorTokens.Primary_100,
}, successLabel, caption, loadingLabel, charLimit = 4, resendTimer = 5, otpAction, onPressResend, metaData, }) => {
    const theme = ThemeProvider.getTheme();
    const colorMapping = theme.colors;
    const otpTextInput = [];
    const [otp, setOtp] = useState([]);
    const [isResend, setIsResend] = useState(false);
    const [otpLimit, setOtplimit] = useState(false);
    const [resetBackground, setResetBackground] = useState(false);
    let timerRef = useRef(null);
    const [countDown, setCountDown] = useState(resendTimer);
    const renderInputs = useCallback(() => {
        const inputs = Array(charLimit).fill(0);
        return inputs.map((i, j) => {
            const _borderColorMapping = (index) => {
                switch (state) {
                    case InputStateToken.ERROR:
                        return border.colorError;
                    case InputStateToken.SUCCESS:
                        return border.colorSuccess;
                    case InputStateToken.DISABLED:
                        return border.color;
                    case InputStateToken.DEFAULT:
                        return resetBackground
                            ? border.color
                            : isEmpty(otp[index])
                                ? border.color
                                : border.colorFocus;
                }
            };
            return (React.createElement(Pressable, { key: metaData
                    ? `${metaData.routeId}.${metaData.widgetId}.${j}`
                    : '', style: [
                    styles.otpBox,
                    {
                        borderColor: colorMapping[_borderColorMapping(j)],
                    },
                ], onPress: () => otpTextInput[j].focus() },
                React.createElement(TextInput, { nativeID: id, value: value, style: [
                        styles.inputRadius,
                        {
                            color: isDarkMode ? colorMapping[ColorTokens.White] : colorMapping[ColorTokens.Grey_Night],
                            fontSize: fontSizeTokensMap[FontSizeTokens.MD],
                        },
                        Platform.OS === 'web' && {
                            //@ts-ignore
                            outlineStyle: 'none',
                        },
                    ], keyboardType: 'numeric', editable: state !== InputStateToken.DISABLED, selectTextOnFocus: state !== InputStateToken.DISABLED, onChangeText: (v) => {
                        /*** reject non numeric values */
                        if (!/^[0-9]*$/.test(v)) {
                            /*** also replace previous value if non-numeric */
                            otpTextInput[i].setNativeProps({
                                text: '',
                            });
                            return;
                        }
                        if (v.length > 1 &&
                            v.length !== charLimit) {
                            /*** Avoid multiple entry in one OTP box ***/
                            otpTextInput[i].setNativeProps({
                                text: String(v)[0],
                            });
                            focusNext(j, v);
                            return;
                        }
                        /*** handle autofill ***/
                        if (v.length === charLimit) {
                            /*** If user pastes OTP, it will be split and filled in the OTP boxes ***/
                            v.split('').map(async (x, i) => {
                                await otpTextInput[i].setNativeProps({
                                    text: x,
                                });
                                /*** request focus on next individual OTP box ***/
                                await focusNext(i, x);
                            });
                        }
                        else {
                            /*** Handle when user is manually typing ***/
                            setOtplimit(true);
                            setLimit(j, v);
                            focusNext(j, v);
                        }
                    }, onKeyPress: (e) => focusPrevious(e.nativeEvent.key, j), autoFocus: j === 0, ref: (ref) => (otpTextInput[j] = ref), 
                    /*** allowing more than one character to be entered in first OTP box for auto sms read for iOS web ***/
                    //maxLength={j === 0 ? charLimit : 1}
                    maxLength: j === 0
                        ? otpLimit === true
                            ? 1
                            : charLimit
                        : 1 })));
        });
    }, [state, charLimit, isResend]);
    useEffect(() => {
        for (let i = 0; i < otpTextInput.length; i++) {
            otpTextInput[i].clear();
        }
        setResetBackground(true);
    }, [isResend]);
    useEffect(() => {
        metaData?.isRouteFocused
            ? startTimer()
            : stopTimer();
        metaData?.isRouteFocused &&
            InteractionManager.runAfterInteractions(() => {
                if (otpTextInput &&
                    otpTextInput.length > 0) {
                    otpTextInput[0]?.focus();
                }
            });
        return stopTimer;
    }, [
        otpLimit,
        isResend,
        metaData?.isRouteFocused,
    ]);
    const focusPrevious = (key, index) => {
        if (key === 'Backspace' && index !== 0) {
            if (index === 1) {
                setOtplimit(true);
            }
            setTimeout(() => otpTextInput[index - 1].focus(), 16);
        }
    };
    const focusNext = (index, value) => {
        if (index === 0 && value.length === 1) {
            setOtplimit(true);
            otpTextInput[index + 1].focus();
            //setLimit();
            //setOtplimit(value);
        }
        if (index < charLimit - 1 && value) {
            otpTextInput[index + 1].focus();
        }
        if (index === charLimit) {
            otpTextInput[index].blur();
        }
        const otpX = otp;
        otpX[index] = value;
        setOtp(otpX);
        handleTextChange(otp.join(''));
    };
    const setLimit = (index, value) => {
        setOtplimit(true);
        if (index === 0 && value.length === 1) {
            setOtplimit(true);
        }
        setTimeout(() => {
            setOtplimit(true);
        }, 0);
    };
    const handleTextChange = (text) => {
        onChangeText && onChangeText(text);
        action &&
            triggerAction &&
            triggerAction({
                ...action,
                payload: {
                    ...action.payload,
                    value: text,
                },
            });
    };
    const handleResend = () => {
        setIsResend(!isResend);
        setResetBackground(true);
        setOtp([]);
        InteractionManager.runAfterInteractions(() => {
            if (otpTextInput && otpTextInput.length > 0) {
                otpTextInput[0]?.focus();
                otpTextInput[0]?.clear();
            }
        });
        onPressResend && onPressResend();
        otpAction &&
            triggerAction &&
            triggerAction(otpAction);
    };
    const startCountDown = () => {
        setCountDown((prevState) => {
            if (prevState > 0) {
                return prevState - 1;
            }
            else {
                stopTimer();
            }
        });
    };
    const startTimer = () => {
        clearInterval(timerRef.current);
        if (!isWeb()) {
            timerRef.current = BackgroundTimer.setInterval(() => {
                startCountDown();
            }, 1000);
        }
        else {
            timerRef.current = setInterval(startCountDown, 1000);
        }
    };
    const stopTimer = () => {
        clearInterval(timerRef.current);
        if (!isWeb()) {
            BackgroundTimer.clearInterval(timerRef.current);
        }
        else {
            clearInterval(timerRef.current);
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Stack, { type: StackType.row, justifyContent: StackJustifyContent.flexStart, alignItems: StackAlignItems.center }, renderInputs()),
        React.createElement(Space, { size: SizeTypeTokens.MD }),
        state === InputStateToken.ERROR && (React.createElement(React.Fragment, null,
            React.createElement(Stack, { type: StackType.row, justifyContent: StackJustifyContent.flexStart, alignItems: StackAlignItems.center },
                React.createElement(Icon, { name: IconTokens.OTPError, color: ColorTokens.System_Error, size: IconSizeTokens.MD }),
                React.createElement(Space, { size: SizeTypeTokens.MD }),
                React.createElement(Typography, { label: caption !== undefined ? caption.error : '', color: ColorTokens.System_Error, fontSize: FontSizeTokens.XS })),
            React.createElement(Space, { size: SizeTypeTokens.XL }))),
        state === InputStateToken.DISABLED && (React.createElement(React.Fragment, null,
            React.createElement(Stack, { type: StackType.row, justifyContent: StackJustifyContent.flexStart, alignItems: StackAlignItems.center },
                React.createElement(Icon, { name: IconTokens.OTPError, color: ColorTokens.System_Error, size: IconSizeTokens.MD }),
                React.createElement(Space, { size: SizeTypeTokens.MD }),
                React.createElement(Typography, { label: caption.error ? caption.error : '', color: ColorTokens.System_Error, fontSize: FontSizeTokens.XS })),
            React.createElement(Space, { size: SizeTypeTokens.XL }))),
        state === InputStateToken.SUCCESS && (React.createElement(Stack, { type: StackType.row, justifyContent: StackJustifyContent.flexStart, alignItems: StackAlignItems.center },
            React.createElement(Icon, { name: IconTokens.Done, color: ColorTokens.System_Happy, size: IconSizeTokens.MD }),
            React.createElement(Space, { size: SizeTypeTokens.MD }),
            React.createElement(Typography, { label: successLabel, color: ColorTokens.System_Happy, fontSize: FontSizeTokens.XS }))),
        state === InputStateToken.LOADING && (React.createElement(Stack, { type: StackType.row, justifyContent: StackJustifyContent.flexStart, alignItems: StackAlignItems.center },
            React.createElement(ActivityIndicator, { size: 16, style: {
                    height: 16,
                    width: 16,
                }, color: colorMapping[ColorTokens.Primary_100] }),
            React.createElement(Space, { size: SizeTypeTokens.MD }),
            React.createElement(Typography, { label: loadingLabel, color: ColorTokens.Primary_100, fontSize: FontSizeTokens.XS }))),
        React.createElement(Space, { size: SizeTypeTokens.SM }),
        countDown > 0 &&
            state !== InputStateToken.LOADING &&
            state !== InputStateToken.SUCCESS && state !== InputStateToken.DISABLED ? (React.createElement(React.Fragment, null,
            React.createElement(Typography, { fontWeight: '400', fontFamily: FontFamilyTokens.Inter, label: `Resend OTP in ${countDown} secs`, color: ColorTokens.Grey_Charcoal, fontSize: FontSizeTokens.XS }),
            React.createElement(Space, { size: SizeTypeTokens.Size32 }))) : (state !== InputStateToken.LOADING &&
            state !== InputStateToken.SUCCESS && state !== InputStateToken.DISABLED && (React.createElement(React.Fragment, null,
            React.createElement(Typography, { fontWeight: '400', fontFamily: FontFamilyTokens.Inter, label: 'Didn’t receive OTP?', color: ColorTokens.Grey_Charcoal, fontSize: FontSizeTokens.XS }),
            React.createElement(Space, { size: SizeTypeTokens.XL }),
            React.createElement(Stack, { type: StackType.row, justifyContent: StackJustifyContent.flexStart, alignItems: StackAlignItems.center, onPress: () => {
                    handleResend();
                    setCountDown(resendTimer);
                    startTimer();
                } },
                React.createElement(Icon, { name: IconTokens.Email, size: IconSizeTokens.MD, color: ColorTokens.Primary_100 }),
                React.createElement(Space, { size: SizeTypeTokens.SM }),
                React.createElement(Typography, { id: 'txt_commonScreen_resendOtp', fontWeight: '400', fontFamily: FontFamilyTokens.Inter, label: 'Resend OTP', color: ColorTokens.Primary_100, fontSize: FontSizeTokens.XS })))))));
});
export default memo(TextInputOtp);
export { TextInputOtp };
const styles = StyleSheet.create({
    gridPad: { padding: 30 },
    txtMargin: { margin: 3 },
    inputRadius: {
        textAlign: 'center',
        height: 48,
        width: 48,
        flexDirection: 'row',
    },
    otpBox: {
        height: 48,
        width: 48,
        marginRight: 8,
        borderWidth: 1,
        borderRadius: 4,
    },
});
